import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { saveAs } from 'file-saver'
import { AgencyDocumentViewerContent } from '@/agent-v2/agency-documents/components/AgencyDocumentViewer'
import { fetchAgencyOriginal } from '@/agent-v2/agency-documents/fetch/agency-document'
import { useAgencyDocument } from '@/agent-v2/agency-documents/hooks/useAgencyDocument'
import { getAgencyOriginalFormat } from '@/agent-v2/agency-documents/utils/agency-original-format'
import { getAgencyPublisherUrl } from '@/agent-v2/agency-documents/utils/get-agency-publisher-url'
import { Button } from '@/components/button/Button'
import { kPaxtonName } from '@/constants/constants-strings'
import { useTitle } from '@/hooks/use-title'

export default function StandaloneAgencyDocumentPage() {
  const { conversationId = '', referenceId = '' } = useParams()
  const state = useAgencyDocument(conversationId, referenceId)
  const publisherUrl = state.data ? getAgencyPublisherUrl(state.data.source_url, state.data.publisher_media_type) : null
  const [downloadError, setDownloadError] = useState(false)
  useTitle(state.data?.title ?? `${kPaxtonName} Reference Viewer`)

  async function download() {
    setDownloadError(false)
    try {
      const original = await fetchAgencyOriginal(conversationId, referenceId)
      saveAs(original.blob, original.fileName || 'decision')
    } catch {
      setDownloadError(true)
    }
  }

  return (
    // `grow` takes the full width of the matter layout row, like the other standalone viewers.
    <div className="flex h-full min-h-0 grow flex-col">
      {state.data && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-neutral-200 p-3">
          <h1 className="text-xl font-bold leading-7 compatible-word-break">{state.data.title}</h1>
          <div className="flex items-center gap-3">
            {state.data.original_available && getAgencyOriginalFormat(state.data.media_type) && (
              <Button variant="outlined" color="neutral" size="sm" onClick={() => void download()}>
                Download original
              </Button>
            )}
            {publisherUrl && (
              <a href={publisherUrl.toString()} target="_blank" rel="noopener noreferrer">
                Official source
              </a>
            )}
          </div>
        </header>
      )}
      {downloadError && <p role="alert">Download failed. Please try again.</p>}
      <div className="min-h-0 flex-1">
        {/* A full-window page would stretch PDFs far past their natural size, so open them at 100%.
            The header above links the official source, so the viewer doesn't repeat it. */}
        <AgencyDocumentViewerContent state={state} pdfDefaultScale={1} showOfficialSource={false} />
      </div>
    </div>
  )
}
